import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';

import { COLOR_TAB_MATERIAL_RIPPLE_COLOR } from '@shared-constants/ui-constants';
import { getProperty } from '@shared-helpers/array-object-helpers';

import { ITabsConfig } from './tabs.model';

@Component({
  selector: 'tabs',
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.css',
  standalone: false,
})
export class TabsComponent<TTabEntity extends object | string> implements OnInit, OnChanges {
  @Output() public readonly tabChanged = new EventEmitter<TTabEntity>();
  @Output() public readonly removeTab = new EventEmitter<string>();
  @Output() public readonly setHorizontalTabsConfigs = new EventEmitter<ITabsConfig<TTabEntity>[]>();

  @Input() public layout: 'row' | 'col' = 'row';
  @Input() public tabWidth: string;
  @Input() public finalTabWidth?: string;
  @Input() public tabs: TTabEntity[] = [];
  @Input() public shouldSeparateFinal = false;
  @Input() public tabType: 'multiLine' | 'singleLine' = 'singleLine';
  @Input() public displayProperty: string;
  @Input() public displayProperty2?: string;
  @Input() public closeButtonProperty?: string;
  @Input() public active?: number;

  public horizontalTabsArray: ITabsConfig<TTabEntity>[] = [];
  public finalTab: ITabsConfig<TTabEntity>;
  public tabsConfig: ITabsConfig<TTabEntity>[] = [];
  public readonly tabsRippleColor = COLOR_TAB_MATERIAL_RIPPLE_COLOR;

  private arraySize = 0; //only needed when separate final is true

  public ngOnChanges(): void {
    this.buildTabs();
    if (this.active) this.setActive(this.tabsConfig[this.active], this.tabsConfig);
  }

  public ngOnInit(): void {
    if (this.tabType === 'multiLine') {
      const horizontalTabsConfig = this.tabsConfig;
      this.horizontalTabsArray = horizontalTabsConfig;
      this.setHorizontalTabsConfigs.emit(horizontalTabsConfig);
    }
    this.buildTabs();
    if (this.active) this.setActive(this.tabsConfig[this.active], this.tabsConfig);
  }

  public setActive(tab: ITabsConfig<TTabEntity>, tabArray: ITabsConfig<TTabEntity>[]): void {
    if (this.shouldSeparateFinal && tab.label === this.finalTab.label) {
      //Set all tabs to be marked as active
      this.tabsConfig.forEach((el) => {
        el.isActive = true;
      });
    } else if (this.horizontalTabsArray.length === 0) {
      tabArray.forEach((el) => {
        el.isActive = tab.label === el.label;
      });
    } else {
      this.horizontalTabsArray.forEach((el) => {
        el.isActive = tab.emitValue === el.emitValue;
      });
    }

    this.tabChanged.emit(tab.emitValue);
  }

  public remove(tabLabel: string): void {
    this.removeTab.emit(tabLabel);
  }

  public setSelectedTab(tab: ITabsConfig<TTabEntity>, tabArray: ITabsConfig<TTabEntity>[]): void {
    this.horizontalTabsArray = tabArray.map((t) => {
      t.isActive = t.label === tab.label;
      return t;
    });
    this.horizontalTabsArray = tabArray;
    this.tabChanged.emit(tab.emitValue);
    this.horizontalTabsArray = [];
  }

  private buildTabs(): void {
    this.tabsConfig = this.tabs.map((value, index) => {
      //Conditional that only BillingReviewComponent will view
      if (typeof value === 'object' && 'displayProperty1' in value && 'displayProperty2' in value) {
        return {
          label: value.displayProperty2 as string,
          label2: value.displayProperty1 as string,
          shouldHideCloseButton: getProperty(value, this.closeButtonProperty, false) as boolean,
          emitValue: value,
          isActive: index === 0,
        };
      }
      if (typeof value === 'string') {
        return {
          label: value,
          emitValue: value,
          isActive: index === 0,
        };
      } else {
        return {
          label: getProperty(value, this.displayProperty, '') as string,
          label2: getProperty(value, this.displayProperty2, undefined) as string | undefined,
          shouldHideCloseButton: getProperty(value, this.closeButtonProperty, false) as boolean,
          emitValue: value,
          isActive: index === 0,
        };
      }
    });

    this.arraySize = this.tabs.length;
    this.finalTab = this.tabsConfig[this.arraySize - 1];
  }
}
